import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  clearAuthStorage,
  resendVerificationEmail,
  verifyEmail,
} from "../api/auth";
import { useAuthUser } from "../components/dashboard/hooks/useAuthUser";
import "./Login-ForgotPassword-ResetPassword.css";
import {
  loginPathWithRedirect,
  sanitizeAuthRedirect,
} from "../utils/authRedirect";

const EmailVerificationPage: React.FC = () => {
  const navigate = useNavigate();
  const { refreshAuth } = useAuthUser();
  const [searchParams] = useSearchParams();
  const redirectTo = sanitizeAuthRedirect(searchParams.get("redirect"));

  const [loading, setLoading] = useState<boolean>(true);
  const [message, setMessage] = useState<string>("");
  const [isSuccess, setIsSuccess] = useState<boolean>(false);

  const [resendEmail, setResendEmail] = useState("");
  const [resendPassword, setResendPassword] = useState("");
  const [resendMessage, setResendMessage] = useState<string | null>(null);
  const [resendError, setResendError] = useState<string | null>(null);
  const [isResending, setIsResending] = useState(false);

  useEffect(() => {
    const token = searchParams.get("token");

    if (!token) {
      setIsSuccess(false);
      setMessage("Invalid or missing verification token.");
      setLoading(false);
      return;
    }

    const run = async () => {
      try {
        const res = await verifyEmail(token);
        clearAuthStorage();
        refreshAuth();
        setIsSuccess(true);
        setMessage(res?.message || "Email verified successfully.");
      } catch (err: unknown) {
        setIsSuccess(false);
        setMessage(
          (err as { response?: { data?: { message?: string } } })?.response
            ?.data?.message ||
            "Verification failed. The link may have expired.",
        );
      } finally {
        setLoading(false);
      }
    };

    void run();
  }, [searchParams, refreshAuth]);

  const handleResend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isResending) return;
    setResendMessage(null);
    setResendError(null);
    setIsResending(true);
    try {
      const result = await resendVerificationEmail({
        email: resendEmail,
        password: resendPassword,
      });
      setResendMessage(result.message);
    } catch (err: unknown) {
      setResendError(
        (err as { response?: { data?: { message?: string } } })?.response
          ?.data?.message ?? "Could not send verification email.",
      );
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-wrapper content-container">
        <div className="wrapper-card pink">
          <div className="info-card login-content">
            <h1>Email verification</h1>

            {loading ? (
              <p>Verifying your email...</p>
            ) : (
              <>
                <div
                  className={`alert ${isSuccess ? "alert-success" : "alert-danger"}`}
                >
                  {message}
                </div>

                {isSuccess && (
                  <button
                    className="btn"
                    type="button"
                    onClick={() =>
                      navigate(loginPathWithRedirect(redirectTo))
                    }
                  >
                    Go to Login
                  </button>
                )}

                {!isSuccess && (
                  <div className="verification-resend-section">
                    <p className="login-resend-hint">
                      Enter your account email and password to receive a new
                      confirmation link.
                    </p>
                    {resendMessage && (
                      <div className="alert alert-success">{resendMessage}</div>
                    )}
                    {resendError && (
                      <div className="alert alert-danger">{resendError}</div>
                    )}
                    <form
                      className="login-form"
                      onSubmit={(e) => void handleResend(e)}
                    >
                      <label htmlFor="resend-email" className="sr-only">
                        Email address
                      </label>
                      <input
                        type="email"
                        id="resend-email"
                        placeholder="Email Address"
                        value={resendEmail}
                        onChange={(e) => setResendEmail(e.target.value)}
                        required
                      />
                      <label htmlFor="resend-password" className="sr-only">
                        Password
                      </label>
                      <input
                        type="password"
                        id="resend-password"
                        placeholder="Password"
                        value={resendPassword}
                        onChange={(e) => setResendPassword(e.target.value)}
                        required
                      />
                      <button
                        type="submit"
                        className="btn"
                        disabled={isResending}
                      >
                        {isResending
                          ? "Sending…"
                          : "Send new verification email"}
                      </button>
                    </form>
                    <button
                      type="button"
                      className="no-style-btn"
                      onClick={() =>
                        navigate(loginPathWithRedirect(redirectTo))
                      }
                    >
                      Back to login
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default EmailVerificationPage;
